import { Link, createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";

import JobItem from "#/components/jobItem";
import SearchBar from "#/components/searchbar";
import Mdx from "#/components/MdxRemote";
import { fmPerson, organization, website } from "#/lib/jsonLdSchema";
import { toAppPath } from "#/lib/router-links";
import { metadataToHead, type PageMetadata } from "#/lib/seo";
import type { ArticleProps } from "#/types/types";

export default function Page() {
  const statesSection = () => {
    return (
      <div>
        {states.map((state) => {
          return (
            <>
              <Link {...toAppPath(`/anesthesiologist-jobs-in-${state.slug}`)}>
                <h3 className="mt-5 font-bold text-sky-500 underline">
                  {`Anesthesiologist Jobs in ${state.name}`}
                </h3>
              </Link>

              <p>{state.description}</p>
            </>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <header>
        <NavBar />
      </header>

      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <article>
          <h1 className="mb-10 px-5 text-center text-5xl">
            Anesthesiologist Salary in the United States
          </h1>

          <section className="mx-8 space-y-5">
            <p>
              The average Anesthesiologist base salary in the United States is{" "}
              <strong>$420,000</strong> and the range is usually between{" "}
              <strong>$370,000</strong> and <strong>$485,000</strong>.
            </p>

            <p>
              Anesthesiologists are in high demand across the United States.
              This not only makes their career fulfilling, but also a
              financially rewarding choice for many.
            </p>

            <p>
              The high demand for anesthesiologists shows just how essential
              they are in surgeries and managing pain. This importance is seen
              in the pay they receive, matching the vital work they do. Now,
              let's explore what affects the salaries of anesthesiologists and
              see what these anesthesia specialists earn in various work
              environments and at different points in their careers.
            </p>
          </section>

          <section className="mx-8">
            <Mdx source={content} />
          </section>

          <aside className="mx-8">
            <Mdx source={contentStateJobs} />

            {statesSection()}

            <SearchBar value="" />

            <section className="mx-8 space-y-5">
              <h2 className="text-xl font-bold">Highest Paying Job Openings</h2>

              <p>
                Find your dream job with our curated list of the most rewarding
                anesthesiologist positions available nationwide.
              </p>

              <div className="space-y-3 p-0">
                {listings.map((job) => {
                  const title = `${job.positionTitle} in ${job.city}`;
                  return <JobItem job={job} title={title} />;
                })}
              </div>
            </section>
          </aside>

          <Mdx source={content2} />
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addJsonLd(["anesthesiologist-salary"], {
            title: `Salary for Anesthesiologist in the United States`,
            metaDescription:
              "The average Anesthesiologist salary in the United States is $420,000 and the range is usually between $370,000 and $485,000.",
          } as ArticleProps)}
          key="product-jsonld"
        />
      </main>
      <Footer fmBio />
    </>
  );
}

export const metadata: PageMetadata = {
  title: `Salary for Anesthesiologist in the United States`,
  description:
    "The average Anesthesiologist salary in the United States is $420,000 and the range is usually between $370,000 and $485,000.",
  openGraph: {
    images: ["https://roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/anesthesiologist-salary`,
  },
};

const content = `
## Locum Tenens Anesthesiologist Hourly Rate

You can earn significantly more per hour as a locum tenens anesthesiologist compared to a full-time, salaried position. 

On average, locum tenens anesthesiologists can expect to earn anywhere from **$280** to **$325 per hour**. It's important to note that these rates are often inclusive of the flexibility and independence associated with locum tenens roles, as well as compensation for the lack of traditional employment benefits such as health insurance and retirement plans.

Furthermore, [locum tenens positions](/locum-anesthesiologist-jobs) often allow anesthesiologists to control their schedules more freely, choose their work environments, and gain experience in a variety of clinical settings. This flexibility can be a significant advantage for those looking to balance work with other personal or professional commitments.

It's also worth considering that locum tenens roles can sometimes involve travel to different locations, which is  compensated separately.

## Highest Paid Anesthesiologist

The highest-paid anesthesiologists are those with extensive experience, specialized skills, or who work in high-demand geographic areas. Additionally, those who manage or own a private practice can have higher earnings due to business revenue, though this comes with additional responsibilities and risks. 

It's important to distinguish Anesthesiologists from other roles in anesthesia, such as nurse anesthetists (CRNA) or anesthesia technicians, who have different training requirements and salary ranges.

1. ### General Anesthesiologists 
   These professionals handle a wide range of procedures in various settings. Their salary typically ranges from approximately $300,000 to $400,000 annually, though this can vary based on location and experience.

1. ### Cardiac Anesthesiologists
   Specialists in cardiac anesthesia, dealing with heart surgery patients, often earn higher salaries due to the complexity and risk involved in these procedures. Their salaries can range from $350,000 to over $450,000.

1. ### Pediatric Anesthesiologists 
    These anesthesiologists specialize in providing anesthesia to infants and children, a role that demands additional training and skills. Salaries for pediatric anesthesiologists can range from $350,000 to $450,000.

1. ### Pain Management Anesthesiologists 
    Focusing on managing chronic pain, these professionals can work both in hospital settings and private clinics. Their salary can vary widely, often between $350,000 and $500,000, depending on their practice setting and patient volume.

1. ### Neuroanesthesiologists 
    Specializing in anesthesia for neurosurgery, these anesthesiologists handle complex and high-risk procedures, often earning between $350,000 and $450,000.

1. ### Obstetric Anesthesiologists 
    Focusing on pain management during childbirth, these professionals can earn between $325,000 and $450,000.

1. ### Critical Care Anesthesiologists
    These anesthesiologists work in critical care units and are involved in a range of intensive care procedures. Their salary typically ranges from $350,000 to $450,000.

1. ### Academic Anesthesiologists
    Those who work in academic or teaching hospitals might have a slightly lower salary range, often between $250,000 and $400,000, but they may find non-monetary benefits in research and teaching opportunities.

## Why do Anesthesiologists make so much money?

Anesthesiologists are among the most highly compensated medical professionals, a reflection of their extensive and specialized training that spans over a decade, including undergraduate education, medical school, and rigorous residency programs. 

Their critical role in ensuring patient safety and managing complex medical situations during surgeries justifies their substantial earnings. Additionally, the high demand for their specialized skills, coupled with the inherent risks and responsibilities of their profession, significantly contributes to their high salary.

1. ### Extensive Education and Training
    Anesthesiologists undergo lengthy and rigorous educational pathways, including four years of undergraduate studies, four years of medical school, and 3-4 years of residency training in anesthesiology. Some also complete additional fellowships in specific sub-specialties. This extensive training is a significant investment of time and resources, justifying higher compensation.

1. ### Critical Role in Patient Care
    Anesthesiologists play a pivotal role in patient safety during surgeries and procedures. They are responsible for administering anesthesia and monitoring the patient's vital signs, managing any complications that arise. This high level of responsibility and the need for precision and skill in their work contribute to their high salaries.

1. ### High Demand and Limited Supply
    There's a consistent demand for anesthesiologists in hospitals, surgical centers, and other medical facilities. However, the supply of these specialists is limited due to the stringent requirements for entering the field. This demand-supply imbalance can drive up salaries.

1. ### Risk and Liability
    Anesthesiology is a high-risk medical specialty. The potential for complications, although low with modern medicine, can be severe, including life-threatening reactions or long-term effects. This risk is often reflected in higher malpractice insurance costs and, consequently, higher compensation.

1. ### Economic Value of Service
    The services provided by anesthesiologists are crucial for the successful completion of surgical procedures. Hospitals and medical practices value these services highly, and this is reflected in the compensation offered to attract and retain skilled anesthesiologists.

1. ### On-Call and Overtime Work
    Anesthesiologists often work irregular hours, including nights, weekends, and holidays. They may also be on call to handle emergencies. This demanding schedule and the need for availability outside of standard working hours often result in higher pay.

1. ### Negotiating Power
    Experienced anesthesiologists, especially those with subspecialty expertise or those working in high-demand areas, often have strong negotiating power when it comes to their contracts and salaries.

`;

const content2 = `
## Where do they get paid the least?

Anesthesiologists tend to receive the lowest compensation in regions with lower costs of living or in areas with a high supply of medical professionals. Rural areas or small towns often offer lower salaries compared to urban centers or regions with a high demand for medical expertise.

## How to become an Anesthesiologist
To become an anesthesiologist, one must first complete a bachelor's degree, with a focus on pre-medical studies or a science-related field. Afterward, aspiring anesthesiologists must attend medical school, which takes four years and leads to a Doctor of Medicine (MD) or Doctor of Osteopathic Medicine (DO) degree. Following medical school, a residency program in anesthesiology, which usually lasts four years, provides specialized training in anesthesia, pain management, and critical care.
`;

const contentStateJobs = `
## Anesthesiologist Job Openings by State

The landscape of [job opportunities for anesthesiologists](/anesthesiologist-jobs) varies significantly across the United States, reflecting regional healthcare demands, population demographics, and economic factors. States with larger populations and extensive healthcare systems, such as California, New York, and Texas, typically offer a higher number of job openings, catering to their dense and diverse patient populations.

In contrast, less populous states might have fewer openings, but they present unique opportunities, such as serving in underserved communities in rural healthcare practices. States like Florida and Arizona, known for their higher retiree populations, also show a strong demand for anesthesiologists, driven by the increased need for medical procedures that necessitate anesthesia services.

Viewing job openings can give you a better idea of the how much an Anesthesiologist can make in your state.`;

const states = [
  {
    name: "Florida",
    slug: "miami-fl",
    description: `In Florida, medical professionals specializing in anesthesia enjoy competitive salaries due to the state's large retiree population, which increases demand for medical procedures requiring their expertise, coupled with the benefit of no state income tax.`,
  },
  {
    name: "California",
    slug: "los-angeles-ca",
    description: `Specialists in California are among the highest paid in the nation, although the state's high cost of living and income tax can significantly impact net income.`,
  },
  {
    name: "New York",
    slug: "new-york-city-ny",
    description: `In New York, specialists in this field typically command high salaries, reflecting the extensive healthcare infrastructure and high cost of living, especially in metropolitan areas.`,
  },
  {
    name: "Texas",
    slug: "houston-tx",
    description: `Texas offers lucrative compensation for these medical specialists, aided by a large and expanding healthcare market and the absence of state income tax, boosting the appeal of the salaries.`,
  },
  {
    name: "Arizona",
    slug: "phoenix-az",
    description: `In Arizona you can expect competitive salaries, driven by the state's aging population and growing healthcare needs, particularly in retiree-dense regions.`,
  },
];

const listings = [
  {
    title: "Pediatric Anesthesiologist at Tucson Children's Hospital",
    city: "Texas",
    hospital: "",
    salary: 550000,
    employmentContractType: "Full-time",
    positionTitle: "Pediatric Anesthesiologist",
    description:
      "Exciting opportunity for a Pediatric Anesthesiologist to make a difference at Tucson Children's Hospital, Tucson, AZ.",
    postedAt: "2023-12-01",
    responsibilities: [
      "Provide anesthesia for pediatric surgical cases",
      "Participate in pediatric pain management clinics",
      "Engage in teaching and mentoring residents",
    ],
    teamDetails: [
      "Collaborate with pediatric surgeons and nursing staff",
      "Supportive environment with focus on continued professional growth",
    ],
    locationDetails: [
      "Tucson is known for its vibrant arts scene, rich history, and diverse cultural heritage",
      "Ideal for families with excellent schools and community-oriented neighborhoods",
    ],
    benefits: [
      "Relocation assistance",
      "Access to state-of-the-art facilities and resources",
    ],
  },
  {
    title: "General Anesthesiologist at Albuquerque Medical Center",
    city: "Florida",
    hospital: "",
    salary: 550000,
    location: "Albuquerque, NM",
    employmentContractType: "W2",
    positionTitle: "General Anesthesiologist",
    startBonus: null,
    description:
      "Join our team as a General Anesthesiologist in the beautiful city of Albuquerque, New Mexico.",
    postedAt: "2023-11-20",
    responsibilities: [
      "Manage general anesthesia cases",
      "Participate in on-call rotation",
      "Collaborate with a multidisciplinary team",
      "Contribute to clinical research projects",
    ],
    teamDetails: [
      "Work with experienced surgeons and nursing staff",
      "Supportive and collaborative work environment",
    ],
    locationDetails: [
      "Albuquerque offers a vibrant cultural scene",
      "Enjoy outdoor activities in the nearby Sandia Mountains",
    ],
    benefits: [
      "Competitive salary",
      "Health, dental, and vision insurance",
      "Retirement plan options",
    ],
  },
  {
    title: "Obstetric Anesthesiologist at Sunshine Women's Hospital",
    city: "California",
    hospital: "",
    salary: 550000,
    location: "Fort Lauderdale, FL",
    employmentContractType: "W2",
    positionTitle: "Obstetric Anesthesiologist",
    startBonus: null,
    description:
      "Join the dedicated team at Sunshine Women's Hospital as an Obstetric Anesthesiologist and provide exceptional care to expectant mothers.",
    postedAt: "2023-11-26",
    responsibilities: [
      "Administer anesthesia for obstetric procedures",
      "Ensure comfort and safety during childbirth",
      "Collaborate with obstetricians and midwives",
    ],
    teamDetails: [
      "Work in a supportive and family-friendly environment",
      "Comprehensive maternal care team",
    ],
    locationDetails: [
      "Fort Lauderdale's beautiful beaches and cultural scene",
      "Florida's renowned women's healthcare services",
    ],
    benefits: [
      "Competitive salary",
      "Paid maternity leave",
      "Flexible scheduling",
    ],
  },
];

function addJsonLd(slug: string[], article: ArticleProps) {
  return {
    __html: `{
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": "https://www.roamdoc.com/${slug.join("/")}/#BlogPosting",
        "url": "https://www.roamdoc.com/${slug.join("/")}",
        "inLanguage": "en-US",
        "headline": "${article.title}",
        "name": "${article.title}",
        "description": "${article.metaDescription}",
        "isPartOf": ${JSON.stringify(website())},
        "author": ${JSON.stringify(fmPerson())},
        "publisher": ${JSON.stringify(organization())},
        "about": [
          {
            "@type": "Thing",
            "name": "anesthesiologist",
            "sameAs": "https://www.wikidata.org/wiki/Q28837"
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "item": {
              "@id": "https://www.roamdoc.com/#BreadcrumbListItem",
              "name": "Roamdoc Home",
              "url": "https://www.roamdoc.com"
            }
          },
          {
            "@type": "ListItem",
            "position": 2,
            "item": {
              "@id": "https://www.roamdoc.com/${slug.join(
                "/",
              )}/#BreadcrumbListItem",
              "name": "${article.title}",
              "url": "https://www.roamdoc.com/${slug.join("/")}"
            }
          }
        ]
      }
    }`,
  };
}

export const Route = createFileRoute("/(salary)/anesthesiologist-salary/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
